/**
 * KinsCircle — Phase 7A Route Recovery Boundary
 *
 * STRICTLY ADDITIVE. ROUTE-LEVEL ERROR ISOLATION.
 *
 * Honors STABILITY.md §1, §13 (read-only-first), §14 (no hidden writes).
 * Phase 7A spec rules:
 *   - NO raw stack traces shown to users.
 *   - Preserves the existing RouteErrorBoundary behavior (this boundary
 *     is meant to nest INSIDE it for high-risk admin / playback routes
 *     when the `routeRecovery` flag is on).
 *   - Pure presentational fallback — never auto-reloads, never silently
 *     mutates app state, never logs PII to a remote endpoint.
 *   - When the `routeRecovery` flag is OFF, this component renders its
 *     children unchanged with no boundary at all.
 *
 * Usage:
 *   <RouteRecoveryBoundary scope="admin/ai">
 *     <AISemanticLab />
 *   </RouteRecoveryBoundary>
 */

import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Home, RefreshCw, ShieldCheck } from 'lucide-react';
import { isRouteRecoveryEnabled } from '@/lib/ai/featureFlag';
import { recordHealthMetric } from '@/lib/system/healthMonitor';

type Props = {
  /** Stable short scope label, e.g. 'admin/ai', 'admin/legacy', 'timeline'. */
  scope: string;
  /** Optional friendly route label for the user-facing message. */
  label?: string;
  children: React.ReactNode;
};

type State = {
  hasError: boolean;
  attempt: number;
  errorName: string | null;
  diagnosticsId: string;
};

const genId = (): string => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const c: any = (typeof globalThis !== 'undefined' && (globalThis as any).crypto) || null;
    if (c?.randomUUID) return String(c.randomUUID()).slice(0, 8);
  } catch {
    /* non-fatal */
  }
  return Math.random().toString(36).slice(2, 10);
};

class RouteRecoveryBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      attempt: 0,
      errorName: null,
      diagnosticsId: genId(),
    };
  }

  static getDerivedStateFromError(error: unknown): Partial<State> {
    let name = 'UnknownError';
    try {
      if (error && typeof error === 'object' && 'name' in error) {
        const n = (error as { name?: unknown }).name;
        if (typeof n === 'string' && n.length > 0) name = n.slice(0, 64);
      }
    } catch {
      /* non-fatal */
    }
    return { hasError: true, errorName: name };
  }

  componentDidCatch(error: unknown, _info: React.ErrorInfo): void {
    // Local-only diagnostics. NO PII, NO message body, NO stack — only the
    // error class name and the static scope identifier.
    try {
      recordHealthMetric({
        kind: 'route_crash',
        scope: this.props.scope?.slice(0, 64) || 'unknown',
        detail: this.state.errorName ?? 'UnknownError',
      });
    } catch {
      /* non-fatal */
    }
    // eslint-disable-next-line no-console
    try {
      console.warn(
        '[RouteRecoveryBoundary] caught error in scope',
        this.props.scope,
        '— rendering safe fallback.',
      );
    } catch {
      /* non-fatal */
    }
  }

  private handleRetry = (): void => {
    this.setState((s) => ({
      hasError: false,
      attempt: s.attempt + 1,
      errorName: null,
      diagnosticsId: genId(),
    }));
  };

  private handleHome = (): void => {
    if (typeof window === 'undefined') return;
    try {
      window.location.assign('/app');
    } catch {
      /* non-fatal */
    }
  };

  render() {
    // Flag OFF → identity wrapper. Existing RouteErrorBoundary still
    // protects the route at a higher level.
    if (!isRouteRecoveryEnabled()) {
      return this.props.children;
    }

    if (!this.state.hasError) {
      // Re-key children on retry so React fully remounts the subtree.
      return (
        <React.Fragment key={`rrb_${this.state.attempt}`}>
          {this.props.children}
        </React.Fragment>
      );
    }

    const label = this.props.label ?? 'this section';
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4 py-10">
        <Card className="max-w-lg w-full p-6 border border-rose-200 bg-rose-50 text-rose-900 rounded-2xl shadow-sm">
          <div className="flex items-start gap-3">
            <AlertTriangle
              className="w-6 h-6 text-rose-700 flex-shrink-0 mt-0.5"
              aria-hidden
            />
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold leading-tight">
                Something went wrong loading {label}.
              </h3>
              <p className="text-sm text-rose-800/90 mt-1 leading-relaxed">
                The rest of KinsCircle is fine. Your family data, memories,
                and uploads are unaffected. You can safely retry this
                section, or head back to your family page.
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  className="bg-rose-700 hover:bg-rose-800 text-white"
                  onClick={this.handleRetry}
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                  Retry
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-rose-300 text-rose-900 hover:bg-rose-100"
                  onClick={this.handleHome}
                >
                  <Home className="w-3.5 h-3.5 mr-1.5" />
                  Return home
                </Button>
              </div>

              <div className="mt-4 flex items-center gap-2 text-[11px] text-rose-700/80">
                <ShieldCheck className="w-3.5 h-3.5" aria-hidden />
                <span>
                  Diagnostics ID: <code className="font-mono">{this.state.diagnosticsId}</code>
                  {' · scope: '}
                  <code className="font-mono">{this.props.scope}</code>
                </span>
              </div>

              <p className="text-[10px] text-rose-700/70 mt-2 leading-relaxed">
                Recovery is local to your browser — nothing is reported off
                device.
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }
}

export default RouteRecoveryBoundary;
export { RouteRecoveryBoundary };
